import { useEffect, useState } from 'react';
import type { Capabilities, Conversation } from '../../shared/types';
import { del, get, post } from '../api';
import { useChat } from '../state';
import { IconClose } from './icons';

interface SettingsView {
  userName: string;
  meshName: string;
  telegramToken: boolean;
  discordToken: boolean;
  livekitUrl: string;
  livekitApiKey: boolean;
  livekitApiSecret: boolean;
  livekitAgentName: string;
  aiBaseUrl: string;
  aiModel: string;
  aiSystemPrompt: string;
  aiApiKey: boolean;
}

type Status = { kind: 'idle' | 'busy' | 'ok' | 'error'; text?: string };

const EMPTY_VIEW: SettingsView = {
  userName: '',
  meshName: '',
  telegramToken: false,
  discordToken: false,
  livekitUrl: '',
  livekitApiKey: false,
  livekitApiSecret: false,
  livekitAgentName: '',
  aiBaseUrl: '',
  aiModel: '',
  aiSystemPrompt: '',
  aiApiKey: false,
};

export function Settings() {
  const open = useChat((state) => state.settingsOpen);
  const toggleSettings = useChat((state) => state.toggleSettings);
  const capabilities = useChat((state) => state.capabilities);
  const peers = useChat((state) => state.peers);
  const upsertConversation = useChat((state) => state.upsertConversation);

  const [view, setView] = useState<SettingsView>(EMPTY_VIEW);
  const [form, setForm] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [telegramToken, setTelegramToken] = useState('');
  const [discordToken, setDiscordToken] = useState('');
  const [chatPlatform, setChatPlatform] = useState<'telegram' | 'discord'>('telegram');
  const [chatId, setChatId] = useState('');
  const [roomName, setRoomName] = useState('');
  const [invite, setInvite] = useState('');

  useEffect(() => {
    if (!open) return;
    get<{ settings: SettingsView }>('/api/settings')
      .then((result) => {
        setView(result.settings);
        setForm({});
      })
      .catch((error: Error) => setStatus({ kind: 'error', text: error.message }));
  }, [open]);

  if (!open) return null;

  const field = (key: string) => form[key] ?? '';

  const save = async () => {
    setStatus({ kind: 'busy', text: 'Saving…' });
    const patch: Record<string, string> = {};
    for (const key of ['userName', 'meshName', 'livekitUrl', 'livekitAgentName', 'aiBaseUrl', 'aiModel', 'aiSystemPrompt']) {
      if (key in form) patch[key] = form[key];
    }
    for (const key of ['livekitApiKey', 'livekitApiSecret', 'aiApiKey']) {
      if (field(key).trim()) patch[key] = form[key];
    }
    try {
      const result = await post<{ settings: SettingsView }>('/api/settings', patch);
      setView(result.settings);
      setForm({});
      setStatus({ kind: 'ok', text: 'Saved' });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const connect = async (platform: 'telegram' | 'discord') => {
    const token = platform === 'telegram' ? telegramToken.trim() : discordToken.trim();
    if (!token) return;
    setStatus({ kind: 'busy', text: `Connecting ${platform}…` });
    try {
      await post(`/api/integrations/${platform}`, { token });
      if (platform === 'telegram') setTelegramToken('');
      else setDiscordToken('');
      setStatus({ kind: 'ok', text: `${platform} connected` });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const disconnect = async (platform: 'telegram' | 'discord') => {
    setStatus({ kind: 'busy', text: `Disconnecting ${platform}…` });
    try {
      await del(`/api/integrations/${platform}`);
      setStatus({ kind: 'ok', text: `${platform} disconnected` });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const addChat = async () => {
    if (!chatId.trim()) return;
    setStatus({ kind: 'busy', text: 'Opening chat…' });
    try {
      const result = await post<{ conversation: Conversation }>('/api/chats', {
        platform: chatPlatform,
        id: chatId.trim(),
      });
      upsertConversation(result.conversation);
      setChatId('');
      setStatus({ kind: 'ok', text: `Added ${result.conversation.title}` });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const createRoom = async () => {
    setStatus({ kind: 'busy', text: 'Creating mesh room…' });
    try {
      const result = await post<{ conversation: Conversation }>('/api/mesh/rooms', {
        name: roomName.trim() || 'Mesh room',
      });
      upsertConversation(result.conversation);
      setRoomName('');
      setStatus({ kind: 'ok', text: `Room ${result.conversation.title} created` });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const joinRoom = async () => {
    if (!invite.trim()) return;
    setStatus({ kind: 'busy', text: 'Joining mesh…' });
    try {
      const result = await post<{ conversation: Conversation }>('/api/mesh/join', { invite: invite.trim() });
      upsertConversation(result.conversation);
      setInvite('');
      setStatus({ kind: 'ok', text: `Joined ${result.conversation.title}` });
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
    }
  };

  const caps: Capabilities | null = capabilities;

  return (
    <div className="modal-backdrop" onClick={() => toggleSettings(false)}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <header className="modal-head">
          <strong>Settings</strong>
          <button className="icon-btn" onClick={() => toggleSettings(false)} aria-label="Close">
            <IconClose />
          </button>
        </header>

        <div className="modal-body">
          <section className="card">
            <h3>You</h3>
            <label>
              Display name
              <input
                value={field('userName') || view.userName}
                onChange={(event) => setForm({ ...form, userName: event.target.value })}
                placeholder="Your name"
              />
            </label>
            <label>
              Mesh peer name
              <input
                value={field('meshName') || view.meshName}
                onChange={(event) => setForm({ ...form, meshName: event.target.value })}
                placeholder="Chatsi peer"
              />
            </label>
            <div className="peers">
              <span className={caps?.mesh.enabled ? 'dot ok' : 'dot'} />
              {caps?.mesh.enabled ? `Peer ${caps.mesh.peerId} · ${caps.mesh.peers} connected` : 'Mesh offline'}
              {peers.filter((peer) => !peer.self).length ? (
                <span className="peer-names">{peers.filter((peer) => !peer.self).map((peer) => peer.name).join(', ')}</span>
              ) : null}
            </div>
          </section>

          <section className="card">
            <h3>
              Telegram <span className={caps?.telegram.enabled ? 'tag ok' : 'tag'}>{caps?.telegram.enabled ? 'connected' : 'off'}</span>
            </h3>
            <p className="hint">
              Bot token from <code>@BotFather</code>. Chats appear once the bot receives a message, or add one by id below.
            </p>
            <div className="row">
              <input
                type="password"
                value={telegramToken}
                onChange={(event) => setTelegramToken(event.target.value)}
                placeholder={view.telegramToken ? 'Token saved — enter a new one to replace' : '123456:ABC-DEF…'}
              />
              <button className="btn" onClick={() => void connect('telegram')} disabled={!telegramToken.trim()}>
                Connect
              </button>
              <button className="btn ghost" onClick={() => void disconnect('telegram')} disabled={!view.telegramToken}>
                Remove
              </button>
            </div>
            {caps?.telegram.detail ? <p className="hint">{caps.telegram.detail}</p> : null}
          </section>

          <section className="card">
            <h3>
              Discord <span className={caps?.discord.enabled ? 'tag ok' : 'tag'}>{caps?.discord.enabled ? 'connected' : 'off'}</span>
            </h3>
            <p className="hint">Bot token, with the Message Content intent enabled in the Discord developer portal.</p>
            <div className="row">
              <input
                type="password"
                value={discordToken}
                onChange={(event) => setDiscordToken(event.target.value)}
                placeholder={view.discordToken ? 'Token saved — enter a new one to replace' : 'Bot token'}
              />
              <button className="btn" onClick={() => void connect('discord')} disabled={!discordToken.trim()}>
                Connect
              </button>
              <button className="btn ghost" onClick={() => void disconnect('discord')} disabled={!view.discordToken}>
                Remove
              </button>
            </div>
            {caps?.discord.detail ? <p className="hint">{caps.discord.detail}</p> : null}
          </section>

          <section className="card">
            <h3>Open a chat</h3>
            <div className="row">
              <select value={chatPlatform} onChange={(event) => setChatPlatform(event.target.value as 'telegram' | 'discord')}>
                <option value="telegram">Telegram</option>
                <option value="discord">Discord</option>
              </select>
              <input
                value={chatId}
                onChange={(event) => setChatId(event.target.value)}
                placeholder={chatPlatform === 'telegram' ? '@channel or chat id' : 'Discord channel id'}
              />
              <button className="btn" onClick={() => void addChat()} disabled={!chatId.trim()}>
                Open
              </button>
            </div>
          </section>

          <section className="card">
            <h3>Pear mesh</h3>
            <p className="hint">Rooms replicate messages peer-to-peer over Hyperswarm. Share the invite code to let a peer join.</p>
            <div className="row">
              <input value={roomName} onChange={(event) => setRoomName(event.target.value)} placeholder="Room name" />
              <button className="btn" onClick={() => void createRoom()}>
                Create
              </button>
            </div>
            <div className="row">
              <input value={invite} onChange={(event) => setInvite(event.target.value)} placeholder="Invite code" />
              <button className="btn" onClick={() => void joinRoom()} disabled={!invite.trim()}>
                Join
              </button>
            </div>
          </section>

          <section className="card">
            <h3>LiveKit calls & agents</h3>
            <div className="row">
              <input
                value={field('livekitUrl') || view.livekitUrl}
                onChange={(event) => setForm({ ...form, livekitUrl: event.target.value })}
                placeholder="wss://your.livekit.cloud"
              />
            </div>
            <div className="row">
              <input
                type="password"
                value={field('livekitApiKey')}
                onChange={(event) => setForm({ ...form, livekitApiKey: event.target.value })}
                placeholder={view.livekitApiKey ? 'API key saved' : 'API key'}
              />
              <input
                type="password"
                value={field('livekitApiSecret')}
                onChange={(event) => setForm({ ...form, livekitApiSecret: event.target.value })}
                placeholder={view.livekitApiSecret ? 'API secret saved' : 'API secret'}
              />
            </div>
            <p className="hint">The voice agent joins as <code>{view.livekitAgentName || 'chatsi-ai'}</code> and answers over text streams.</p>
          </section>

          <section className="card">
            <h3>
              AI <span className={caps?.ai.enabled ? 'tag ok' : 'tag'}>{caps?.ai.enabled ? caps.ai.model : 'off'}</span>
            </h3>
            <div className="row">
              <input
                value={field('aiBaseUrl') || view.aiBaseUrl}
                onChange={(event) => setForm({ ...form, aiBaseUrl: event.target.value })}
                placeholder="https://api.openai.com/v1"
              />
              <input
                value={field('aiModel') || view.aiModel}
                onChange={(event) => setForm({ ...form, aiModel: event.target.value })}
                placeholder="gpt-4o-mini"
              />
            </div>
            <div className="row">
              <input
                type="password"
                value={field('aiApiKey')}
                onChange={(event) => setForm({ ...form, aiApiKey: event.target.value })}
                placeholder={view.aiApiKey ? 'API key saved' : 'API key'}
              />
            </div>
            <label>
              System prompt
              <textarea
                rows={3}
                value={field('aiSystemPrompt') || view.aiSystemPrompt}
                onChange={(event) => setForm({ ...form, aiSystemPrompt: event.target.value })}
                placeholder="You are Chatsi, a concise and friendly assistant."
              />
            </label>
          </section>
        </div>

        <footer className="modal-foot">
          <span className={`status ${status.kind}`}>{status.text}</span>
          <button className="btn" onClick={() => void save()} disabled={status.kind === 'busy'}>
            Save changes
          </button>
          <button className="btn ghost" onClick={() => toggleSettings(false)}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
